import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { shape, number, arrayOf, string, node, func } from 'prop-types'

import { Marker, GoogleApiWrapper, InfoWindow } from 'google-maps-react'

import { CircularProgress, Backdrop, Hidden } from '@material-ui/core'

import env from '@config/env'

import useLoading from '@hooks/useLoading'

import Zoom from './Components/ZoomButtons'
import Center from './Components/CenterButton'
import PanelWithPhoto from './Components/PanelWithPhoto'
import SearchInArea from './Components/SearchInArea'

import mapStyles, {
  ButtonsWrappers,
  Map,
  BackdropWrapper,
  SearchWrappers,
  InfoWindowForMobile,
} from './Map.style'

function MapContainer({
  google,
  position,
  markers,
  icon,
  selectedIcon,
  onSelectMarker,
  onUnselectMarker,
  onSearch,
  selectedGym,
  children,
  icons,
  theme,
}) {
  const intl = useIntl()
  const { isLoading, featureLoading } = useLoading()
  const [currentMarker, setCurrentMarker] = useState(null)
  const [map, setMap] = useState(null)
  const [showSearch, setShowSearch] = useState(false)

  function onReady(_, googleMap) {
    googleMap.setOptions({
      styles: mapStyles,
      language: 'pt',
    })

    setMap(googleMap)
  }

  function onMapClick() {
    if (currentMarker) {
      setCurrentMarker(null)
      onUnselectMarker()
    }
  }

  function onGymClicked({ item }, marker) {
    setCurrentMarker(marker)
    onSelectMarker(item)
  }

  function onSearchAction() {
    onSearch({ lat: map.center.lat(), lng: map.center.lng() })
    setShowSearch(false)
  }

  function onDragEnd() {
    if (!showSearch) {
      setShowSearch(true)
    }
  }

  useEffect(() => {
    if (map && position) {
      map.setCenter(position)
    }
  }, [position])
  return (
    <Map
      google={google}
      onReady={onReady}
      onClick={onMapClick}
      onDragend={onDragEnd}
      zoom={15}
      zoomControl={false}
      mapTypeControl={false}
      scaleControl={false}
      streetViewControl={false}
      panControl={false}
      rotateControl={false}
      fullscreenControl={false}
      initialCenter={position}
    >
      <BackdropWrapper>
        <Backdrop open={isLoading && featureLoading === 'GymSearchMap'}>
          <CircularProgress color="inherit" />
        </Backdrop>
      </BackdropWrapper>

      {markers.map(marker => (
        <Marker
          key={marker.id}
          position={marker.position}
          title={marker.title}
          item={marker}
          onClick={onGymClicked}
          icon={{
            url: marker.selected ? selectedIcon : icon,
          }}
        />
      ))}
      <InfoWindow visible={!!selectedGym} marker={currentMarker}>
        {selectedGym ? (
          <PanelWithPhoto
            title={selectedGym.title}
            photoUrl={selectedGym.photoUrl}
            activities={selectedGym.activities}
            rating={selectedGym.rating}
            slug={selectedGym.slug}
          />
        ) : (
          <div />
        )}
      </InfoWindow>

      <Hidden lgUp>
        {selectedGym && (
          <InfoWindowForMobile>
            <PanelWithPhoto
              title={selectedGym.title}
              photoUrl={selectedGym.photoUrl}
              activities={selectedGym.activities}
              rating={selectedGym.rating}
              slug={selectedGym.slug}
            />
          </InfoWindowForMobile>
        )}
      </Hidden>

      {map && (
        <>
          {children}

          <ButtonsWrappers hasSelectedGym={!!selectedGym}>
            <Center map={map} position={position} icon={icons.aim} />
            <Zoom
              map={map}
              zoomInIcon={icons.zoomIn}
              zoomOutIcon={icons.zoomOut}
            />
          </ButtonsWrappers>

          {showSearch && (
            <SearchWrappers>
              <SearchInArea
                displayText={
                  intl.messages[
                    'smb_acquisition_gym_search_map.call_to_action.search_in_this_area'
                  ]
                }
                onSearch={onSearchAction}
                theme={theme.searchInArea}
              />
            </SearchWrappers>
          )}
        </>
      )}
    </Map>
  )
}

MapContainer.propTypes = {
  children: node,
  google: shape(),
  position: shape({
    lat: number,
    lng: number,
  }).isRequired,
  markers: arrayOf(
    shape({
      id: number,
      title: string,
      location: shape({
        lat: number,
        lng: number,
      }),
    })
  ),
  icon: string.isRequired,
  selectedIcon: string.isRequired,
  onSelectMarker: func.isRequired,
  onUnselectMarker: func.isRequired,
  selectedGym: shape({
    gym: shape({
      id: number,
    }),
  }),
  onSearch: func.isRequired,
  icons: shape({
    gymMarker: string,
    gymMarkerSelected: string,
    search: string,
    filter: string,
    aim: string,
    zoomIn: string,
    zoomOut: string,
  }),
  theme: shape({
    searchInArea: shape({
      buttonText: shape({
        color: string,
      }),
    }),
  }),
}

MapContainer.defaultProps = {
  google: null,
  children: null,
  markers: [],
  selectedGym: null,
  icons: {},
  theme: {
    searchInArea: {
      buttonText: {
        color: '',
      },
    },
  },
}

export default GoogleApiWrapper(({ language }) => ({
  apiKey: env.googleServicesApiKey,
  language,
}))(MapContainer)
